import React, { forwardRef, useContext, useLayoutEffect, useRef } from "react";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import Portal from "../../layout/Portal";
import TagWrapper from "../TagWrapper";
import useEventListener from "../../../Hooks/useEventListener";
import ThemeContext from "../../../Contexts/themeContext";
import useDeviceScreen from "../../../Hooks/useDeviceScreen";

export const OffCanvasTitle = forwardRef(
  ({ tag = "h5", id, children, className, ...props }, ref) => {
    return (
      <TagWrapper
        tag={tag}
        ref={ref}
        id={id}
        className={classNames("offcanvas-title", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const OffCanvasHeader = forwardRef(
  ({ children, className, setOpen = () => {}, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames("offcanvas-header", className)}
        {...props}
      >
        {children}
        {setOpen && (
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => setOpen(false)}
          />
        )}
      </div>
    );
  }
);

export const OffCanvasBody = forwardRef(
  ({ tag = "div", children, className, ...props }, ref) => {
    return (
      <TagWrapper
        tag={tag}
        ref={ref}
        className={classNames("offcanvas-body", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

const OffCanvas = ({
  id,
  titleId,
  children,
  placement = "end",
  isBodyScroll,
  isBackdrop = true,
  isModalStyle,
  isOpen,
  setOpen = () => {},
  isRightPanel,
  tag: Tag = "div",
  className,
  ...props
}) => {
  const { mobileDesign, setRightPanel } = useContext(ThemeContext);
  const deviceScreen = useDeviceScreen();

  const initialProps = {
    isBackdrop: isRightPanel && !mobileDesign ? false : isBackdrop,
    isBodyScroll: isRightPanel && !mobileDesign ? true : isBodyScroll,
    placement: isRightPanel ? "end" : placement,
  };

  useLayoutEffect(() => {
    setRightPanel &&
      setRightPanel(isRightPanel && deviceScreen?.width > 1200 && isOpen);
  }, [deviceScreen?.width, isOpen, isRightPanel, setRightPanel]);

  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!initialProps.isBodyScroll && isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0px";
    }
    return () => {
      document.body.style.overflow = "auto";
      document.body.style.removeProperty("padding-right");
    };
  }, [isOpen, initialProps.isBodyScroll]);

  const closeCanvas = (event) => {
    if (
      ref.current &&
      !ref.current.contains(event.target) &&
      ((!isRightPanel && isBackdrop) || (isRightPanel && mobileDesign))
    ) {
      setOpen(false);
    }
  };

  useEventListener("mousedown", closeCanvas);
  useEventListener("touchstart", closeCanvas);

  const PLACEMENT_ANIMATION =
    (initialProps.placement === "start" && { x: "-100%" }) ||
    (initialProps.placement === "top" && { y: "-100%" }) ||
    (initialProps.placement === "bottom" && { y: "100%" }) || { x: "100%" };

  const MotionTagWrapper = motion[Tag];
  return (
    <Portal>
      <AnimatePresence mode="wait">
        {isOpen && (
          <>
            <MotionTagWrapper
              ref={ref}
              key="offCanvas"
              initial={{ opacity: 0, ...PLACEMENT_ANIMATION }}
              animate={{ opacity: 1, x: "0%", y: "0%" }}
              exit={{ opacity: 0, ...PLACEMENT_ANIMATION }}
              transition={{ ease: "easeInOut", duration: 0.3 }}
              id={id}
              className={classNames(
                "offcanvas",
                className,
                `offcanvas-${initialProps.placement}`,
                {
                  show: isOpen,
                  "offcanvas-modal-style": isModalStyle,
                  "offcanvas-right-panel":
                    isRightPanel && deviceScreen?.width > 1200,
                }
              )}
              tabIndex={-1}
              aria-labelledby={titleId}
              data-bs-scroll={initialProps.isBodyScroll}
              data-bs-backdrop={initialProps.isBackdrop}
              style={{ visibility: isOpen && "visible" }}
              {...props}
            >
              {children}
            </MotionTagWrapper>
            {initialProps.isBackdrop && (
              <div className={classNames("offcanvas-backdrop", "fade", "show")} />
            )}
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default OffCanvas;
